import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import {
  AssociatedItem,
  SegmentBEFields,
  SegmentData,
  SegmentDataResponse,
  SegmentMemberItem,
} from "types/segments";
import { getEnvVariable } from "utilities/env";

export const useSegments = (data?: { segmentId?: string }) => {
  const { code: tenant } = useAppSelector(selectCurrentStoreData);
  const { fetcher, arrayFetcher } = useAuth();
  const { segmentId } = data || {};

  const useSegmentsList = (props?: ListingProps) => {
    return useSWR<ListResponse<SegmentData>>(
      () =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteSegmentsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<SegmentData>>(
      (index) =>
        tenant
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useSegmentAssociatedCampaigns = (
    segmentId: string,
    props?: ListingProps,
  ) => {
    return useSWR<ListResponse<AssociatedItem>>(
      () =>
        tenant && segmentId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment/${segmentId}/association/campaign${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useSegmentAssociatedRewards = (
    segmentId: string,
    props?: ListingProps,
  ) => {
    return useSWR<ListResponse<AssociatedItem>>(
      () =>
        tenant && segmentId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment/${segmentId}/association/reward${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useSegmentAssociatedAchievements = (
    segmentId: string,
    props?: ListingProps,
  ) => {
    return useSWR<ListResponse<AssociatedItem>>(
      () =>
        tenant && segmentId
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment/${segmentId}/association/achievement${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useMembersInSegmentList = (props?: ListingProps) => {
    return useSWR<ListResponse<SegmentMemberItem>>(
      () =>
        tenant && segmentId !== ""
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${tenant}/segment/${segmentId}/members${querystring(props)}`
          : null,
      fetcher,
    );
  };
  const addSegment = async (
    payload: SegmentBEFields,
  ): Promise<{ data: { segmentId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/segment`,
      {
        body: JSON.stringify({ segment: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };
  const editSegment = async (
    segmentId: string,
    { ...payload }: SegmentBEFields,
  ): Promise<{ data: { segmentId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/segment/${segmentId}`,
      {
        body: JSON.stringify({ segment: payload }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };
  const useSegmentData = <T extends string | string[]>(
    segmentID?: T,
  ): SWRResponse<SegmentDataResponse<T>> => {
    const isSingleRequest = typeof segmentID === "string";

    return useSWR(
      () =>
        tenant && segmentID
          ? isSingleRequest
            ? `${getEnvVariable(
                "REACT_APP_API_URL",
              )}/${tenant}/segment/${segmentID}`
            : segmentID.map(
                (segment) =>
                  `${getEnvVariable(
                    "REACT_APP_API_URL",
                  )}/${tenant}/segment/${segment}`,
              )
          : null,
      isSingleRequest ? fetcher : arrayFetcher,
    );
  };
  const activateSegment = async (segmentID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/segment/${segmentID}/activate`,
      { method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const deactivateSegment = async (segmentID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${tenant}/segment/${segmentID}/deactivate`,
      { method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };
  const removeSegment = async (segmentID: string) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/segment/${segmentID}`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const importSegmentMembers = async (
    segmentId: string,
    file?: string | Blob,
    identificationMethod?: string,
  ): Promise<{ data: { importId: string } }> => {
    const formData = new FormData();
    file && formData.append("import[file]", file);

    segmentId &&
      formData.append("import[additionalData][segmentId]", segmentId);

    identificationMethod &&
      formData.append(
        "import[additionalData][identificationMethod]",
        identificationMethod,
      );

    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/import/segmentMembers`,
      { body: formData, method: "post" },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  return {
    activateSegment,
    addSegment,
    deactivateSegment,
    editSegment,
    removeSegment,
    useMembersInSegmentList,
    useSegmentData,
    useSegmentsList,
    importSegmentMembers,
    useInfiniteSegmentsList,
    useSegmentAssociatedCampaigns,
    useSegmentAssociatedRewards,
    useSegmentAssociatedAchievements,
  };
};
